// Copyright (c) Docugami, Inc. All rights reserved.

import { TextField } from "@fluentui/react"
import { DgButton } from "components/common/DgCommonStyledComponents"
import { dgFontFamily } from "components/common/Fonts"
import { ExplorerTextButton } from "components/myContentExplorer/ExplorerStyles"
import { emptyGray, homeGray } from "components/shell/presentation/AppStyles"
import { Telemetry } from 'telemetry/TelemetryManager';
import { APIKeyCreatedSchema, TelemetryEvent } from 'telemetry/TelemetryEvents';
import { AppState } from '../../store';
import { useState } from "react"
import { useSelector } from 'react-redux';
import { useDgAuth } from 'hooks/DgAuthentication';
import styled from "styled-components"

const Text = styled.span`
    white-space: pre-wrap;
    font-size: 20px;
    font-weight: 500;
    font-family: ${dgFontFamily};
    color: ${homeGray};
`

const InputSection = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    align-items: flex-end;
    gap: 15px;
`

const Root = styled.div`
    display: flex;
    flex-direction: column;
    gap: 25px;
    width: 100%;
    border-bottom: 1px solid ${emptyGray};
    padding: 20px 40px;
    box-sizing: border-box;
`

const CreateButton = styled(DgButton)`
    padding: 4px 8px;
    height: fit-content;
`
const CancelButton = styled(ExplorerTextButton)`
    height: fit-content;
    margin-left: auto;
`

export interface CreateApiKeySectionProps {
    onCancel: () => void;
    onCreate: (name: string) => void;
}

export const CreateApiKeySection = (props: CreateApiKeySectionProps) => {
    const [text, setText] = useState<string>();

    const { user } = useDgAuth();
    const username = user?.name ?? '';
    const orgName = useSelector((state: AppState) => state.organizations?.currentOrganization?.name);

    function onCreateButtonClick() {
        Telemetry.trackEvent<APIKeyCreatedSchema>({
            user: username,
            workspace: orgName,
            tokenName: text,
            eventName: TelemetryEvent.APIKeyCreated
        });
        props.onCreate(text!);
    }

    return (
        <Root>
            <Text>
                {createTokenText}
            </Text>
            <InputSection>
                <TextField
                    autoComplete="off"
                    value={text}
                    onChange={(ev, value) => setText(value)}
                    label="NAME:"
                    styles={{
                        root: {
                            width: '65%'
                        },
                        field: {
                            fontFamily: dgFontFamily
                        }
                    }} />
                <CancelButton onClick={props.onCancel}>Cancel</CancelButton>
                <CreateButton disabled={!text} onClick={() => onCreateButtonClick()}>DONE</CreateButton>
            </InputSection>
        </Root>
    )
}

const createTokenText = `Personal access tokens are required to use the Docugami API.

This token will grant access to data in your workspace. This token is visible to other admins in your workspace. Only use this token for your own development. Don’t share it with third-party services and applications.

The token’s name will be visible in record revision history.
`
